import React, { useEffect, useState } from "react";
import VsOpsLayout from "./VsOpsLayout";
import { VsOpsRequireAuth } from "./VsOpsLoginPage";
import { vsOpsApi } from "./api";
import { formatRubPrice, formatUsdtPrice } from "../visoria/helpers";

function formatWithdrawalAmount(row) {
  if (row.paymentMethod === "CRYPTO") {
    return formatUsdtPrice(row.amountUsdt);
  }
  return formatRubPrice(row.amountRub);
}

function ProfitCards({ title, totals, formatMoney }) {
  const isCrypto = totals.currency === "USDT";
  const periodProfit = isCrypto ? totals.profitUsdt : totals.profitRub;
  const periodWithdrawn = isCrypto
    ? totals.periodWithdrawnUsdt
    : totals.periodWithdrawnRub;
  const lifetimeWithdrawn = isCrypto ? totals.withdrawnUsdt : totals.withdrawnRub;
  const lifetimeProfit = isCrypto
    ? totals.lifetimeProfitUsdt
    : totals.lifetimeProfitRub;
  const available = isCrypto
    ? totals.availableUsdt ?? totals.netProfitUsdt
    : totals.availableRub ?? totals.netProfitRub;

  return (
    <section className="vs-ops-withdraw-section">
      <h2 className="vs-ops-subtitle">{title}</h2>
      <p className="vs-ops-muted">
        Карточки оборота и прибыли — за выбранный период. «К выводу» — остаток
        за всё время (вся прибыль минус все выводы), не зависит от дат фильтра.
      </p>
      <div className="vs-ops-cards">
        <div className="vs-ops-card">
          <span className="vs-ops-card-label">Оборот за период</span>
          <strong>
            {formatMoney(
              isCrypto ? totals.revenueUsdt : totals.revenueRub,
            )}
          </strong>
        </div>
        <div className="vs-ops-card">
          <span className="vs-ops-card-label">
            {isCrypto ? "Комиссия крипты" : "Комиссия СБП"}
          </span>
          <strong>
            {formatMoney(
              isCrypto ? totals.paymentFeeUsdt : totals.paymentFeeRub,
            )}
          </strong>
        </div>
        <div className="vs-ops-card">
          <span className="vs-ops-card-label">После комиссии</span>
          <strong>
            {formatMoney(
              isCrypto ? totals.revenueNetUsdt : totals.revenueNetRub,
            )}
          </strong>
        </div>
        <div className="vs-ops-card">
          <span className="vs-ops-card-label">Себестоимость</span>
          <strong>
            {formatMoney(isCrypto ? totals.costUsdt : totals.costRub)}
          </strong>
        </div>
        <div className="vs-ops-card">
          <span className="vs-ops-card-label">Прибыль за период</span>
          <strong>{formatMoney(periodProfit)}</strong>
        </div>
        <div className="vs-ops-card">
          <span className="vs-ops-card-label">Выведено за период</span>
          <strong>{formatMoney(periodWithdrawn ?? 0)}</strong>
        </div>
        <div className="vs-ops-card">
          <span className="vs-ops-card-label">Прибыль всего</span>
          <strong>{formatMoney(lifetimeProfit ?? periodProfit)}</strong>
        </div>
        <div className="vs-ops-card">
          <span className="vs-ops-card-label">Выведено всего</span>
          <strong>{formatMoney(lifetimeWithdrawn ?? 0)}</strong>
        </div>
        <div className="vs-ops-card vs-ops-card-accent">
          <span className="vs-ops-card-label">К выводу (не выведено)</span>
          <strong>{formatMoney(available)}</strong>
        </div>
      </div>
    </section>
  );
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function monthStartIso() {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10);
}

function formatDay(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString("ru-RU");
}

function VsOpsStatsPage() {
  const [filters, setFilters] = useState({
    from: monthStartIso(),
    to: todayIso(),
    storeId: "",
    regionId: "",
    groupBy: "day",
  });
  const [turnover, setTurnover] = useState({ sbp: { items: [] }, crypto: { items: [] } });
  const [profit, setProfit] = useState(null);
  const [withdrawals, setWithdrawals] = useState({ items: [] });
  const [withdrawMethod, setWithdrawMethod] = useState("SBP");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawNote, setWithdrawNote] = useState("");
  const [withdrawDate, setWithdrawDate] = useState(todayIso());
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawMessage, setWithdrawMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => String(value).trim()),
      );
      const [turnoverData, profitData, withdrawalsData] = await Promise.all([
        vsOpsApi.getTurnover(params),
        vsOpsApi.getProfit(params),
        vsOpsApi.getWithdrawals(params),
      ]);
      setTurnover(turnoverData);
      setProfit(profitData);
      setWithdrawals(withdrawalsData);
    } catch (loadError) {
      setError(loadError.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleWithdraw = async (event) => {
    event.preventDefault();
    setWithdrawLoading(true);
    setWithdrawMessage("");
    try {
      await vsOpsApi.createWithdrawal({
        paymentMethod: withdrawMethod,
        amountRub: withdrawMethod === "SBP" ? withdrawAmount : undefined,
        amountUsdt: withdrawMethod === "CRYPTO" ? withdrawAmount : undefined,
        note: withdrawNote,
        withdrawnAt: withdrawDate,
      });
      setWithdrawAmount("");
      setWithdrawNote("");
      setWithdrawDate(todayIso());
      setWithdrawMessage("Вывод записан");
      await load();
    } catch (withdrawError) {
      setWithdrawMessage(withdrawError.message || "Не удалось записать вывод");
    } finally {
      setWithdrawLoading(false);
    }
  };

  const sbpDailyTotals = turnover.sbp?.dailyTotals || [];
  const cryptoDailyTotals = turnover.crypto?.dailyTotals || [];

  return (
    <VsOpsRequireAuth>
      <VsOpsLayout title="Обороты и прибыль">
        <form
          className="vs-ops-filters"
          onSubmit={(event) => {
            event.preventDefault();
            load();
          }}
        >
          <label>
            С
            <input
              type="date"
              value={filters.from}
              onChange={(event) =>
                setFilters((current) => ({ ...current, from: event.target.value }))
              }
            />
          </label>
          <label>
            По
            <input
              type="date"
              value={filters.to}
              onChange={(event) =>
                setFilters((current) => ({ ...current, to: event.target.value }))
              }
            />
          </label>
          <label>
            Магазин
            <input
              value={filters.storeId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, storeId: event.target.value }))
              }
              placeholder="nintendo"
            />
          </label>
          <label>
            Регион
            <input
              value={filters.regionId}
              onChange={(event) =>
                setFilters((current) => ({ ...current, regionId: event.target.value }))
              }
              placeholder="usd"
            />
          </label>
          <label>
            Группировка
            <select
              value={filters.groupBy}
              onChange={(event) =>
                setFilters((current) => ({ ...current, groupBy: event.target.value }))
              }
            >
              <option value="day">По дням + магазин/регион</option>
              <option value="day-total">По дням (все магазины)</option>
              <option value="store">По магазинам</option>
              <option value="region">По магазину + региону</option>
              <option value="total">Итого</option>
            </select>
          </label>
          <button type="submit" className="vs-ops-btn" disabled={loading}>
            Обновить
          </button>
        </form>

        {error ? <p className="vs-ops-error">{error}</p> : null}

        {profit?.sbp ? (
          <ProfitCards
            title="СБП — обороты и прибыль"
            totals={profit.sbp.totals}
            formatMoney={formatRubPrice}
          />
        ) : null}

        {profit?.crypto ? (
          <ProfitCards
            title="Крипта — обороты и прибыль"
            totals={profit.crypto.totals}
            formatMoney={formatUsdtPrice}
          />
        ) : null}

        {profit ? (
          <section className="vs-ops-withdraw-section">
            <h2 className="vs-ops-subtitle">Записать вывод</h2>
            <form className="vs-ops-filters" onSubmit={handleWithdraw}>
              <label>
                Тип вывода
                <select
                  value={withdrawMethod}
                  onChange={(event) => setWithdrawMethod(event.target.value)}
                >
                  <option value="SBP">СБП (₽)</option>
                  <option value="CRYPTO">Крипта (USDT)</option>
                </select>
              </label>
              <label>
                {withdrawMethod === "CRYPTO" ? "Сумма, USDT" : "Сумма, ₽"}
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={withdrawAmount}
                  onChange={(event) => setWithdrawAmount(event.target.value)}
                  placeholder={withdrawMethod === "CRYPTO" ? "10.50" : "100"}
                  required
                />
              </label>
              <label>
                Дата
                <input
                  type="date"
                  value={withdrawDate}
                  onChange={(event) => setWithdrawDate(event.target.value)}
                  required
                />
              </label>
              <label>
                Комментарий
                <input
                  value={withdrawNote}
                  onChange={(event) => setWithdrawNote(event.target.value)}
                  placeholder="Вывод на счёт"
                />
              </label>
              <button type="submit" className="vs-ops-btn" disabled={withdrawLoading}>
                {withdrawLoading ? "Сохраняем…" : "Записать вывод"}
              </button>
            </form>
            {withdrawMessage ? <p className="vs-ops-muted">{withdrawMessage}</p> : null}

            <h2 className="vs-ops-subtitle">История выводов</h2>
            <p className="vs-ops-muted">
              Список ниже фильтруется выбранными датами. Остаток «К выводу» выше —
              за всё время.
            </p>
            <p className="vs-ops-table-scroll-hint">Прокрутите таблицу вправо →</p>
            <div className="vs-ops-table-wrap">
              <table className="vs-ops-table vs-ops-table--compact">
                <thead>
                  <tr>
                    <th>Тип</th>
                    <th>Дата</th>
                    <th>Сумма</th>
                    <th>Комментарий</th>
                    <th>Кто записал</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.items.length ? (
                    withdrawals.items.map((row) => (
                      <tr key={row.id}>
                        <td>{row.paymentMethod === "CRYPTO" ? "Крипта" : "СБП"}</td>
                        <td>{new Date(row.withdrawnAt).toLocaleDateString("ru-RU")}</td>
                        <td>{formatWithdrawalAmount(row)}</td>
                        <td>{row.note || "—"}</td>
                        <td>{row.withdrawnByAdmin || "—"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>За выбранный период выводов нет</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        <h2 className="vs-ops-subtitle">Оборот по дням — все магазины (СБП)</h2>
        <p className="vs-ops-table-scroll-hint">Прокрутите таблицу вправо →</p>
        <div className="vs-ops-table-wrap">
          <table className="vs-ops-table vs-ops-table--compact">
            <thead>
              <tr>
                <th>День</th>
                <th>Заказов</th>
                <th>Оборот ₽</th>
                <th>USD</th>
              </tr>
            </thead>
            <tbody>
              {sbpDailyTotals.length ? (
                sbpDailyTotals.map((row) => (
                  <tr key={`sbp-day-${row.day}`}>
                    <td>{formatDay(row.day)}</td>
                    <td>{row.ordersCount}</td>
                    <td>{formatRubPrice(row.turnoverRub)}</td>
                    <td>{row.turnoverUsd ?? "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>Нет данных за период</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <h2 className="vs-ops-subtitle">Оборот по дням — все магазины (крипта)</h2>
        <p className="vs-ops-table-scroll-hint">Прокрутите таблицу вправо →</p>
        <div className="vs-ops-table-wrap">
          <table className="vs-ops-table vs-ops-table--compact">
            <thead>
              <tr>
                <th>День</th>
                <th>Заказов</th>
                <th>Оборот USDT</th>
                <th>USD (Visoria)</th>
              </tr>
            </thead>
            <tbody>
              {cryptoDailyTotals.length ? (
                cryptoDailyTotals.map((row) => (
                  <tr key={`crypto-day-${row.day}`}>
                    <td>{formatDay(row.day)}</td>
                    <td>{row.ordersCount}</td>
                    <td>{formatUsdtPrice(row.turnoverUsdt)}</td>
                    <td>{row.turnoverUsd ?? "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>Нет данных за период</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <h2 className="vs-ops-subtitle">Оборот СБП (детализация)</h2>
        <p className="vs-ops-table-scroll-hint">Прокрутите таблицу вправо →</p>
        <div className="vs-ops-table-wrap">
          <table className="vs-ops-table vs-ops-table--compact">
            <thead>
              <tr>
                <th>Период / магазин</th>
                <th>Регион</th>
                <th>Заказов</th>
                <th>Оборот ₽</th>
                <th>USD</th>
              </tr>
            </thead>
            <tbody>
              {(turnover.sbp?.items || []).map((row, index) => (
                <tr key={`sbp-${row.day || row.storeId}-${row.regionId || ""}-${index}`}>
                  <td>{row.day || row.storeId || "Итого"}</td>
                  <td>{row.regionId || "—"}</td>
                  <td>{row.ordersCount}</td>
                  <td>{formatRubPrice(row.turnoverRub)}</td>
                  <td>{row.turnoverUsd ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="vs-ops-subtitle">Оборот крипта (детализация)</h2>
        <p className="vs-ops-table-scroll-hint">Прокрутите таблицу вправо →</p>
        <div className="vs-ops-table-wrap">
          <table className="vs-ops-table vs-ops-table--compact">
            <thead>
              <tr>
                <th>Период / магазин</th>
                <th>Регион</th>
                <th>Заказов</th>
                <th>Оборот USDT</th>
                <th>USD (Visoria)</th>
              </tr>
            </thead>
            <tbody>
              {(turnover.crypto?.items || []).map((row, index) => (
                <tr key={`crypto-${row.day || row.storeId}-${row.regionId || ""}-${index}`}>
                  <td>{row.day || row.storeId || "Итого"}</td>
                  <td>{row.regionId || "—"}</td>
                  <td>{row.ordersCount}</td>
                  <td>{formatUsdtPrice(row.turnoverUsdt)}</td>
                  <td>{row.turnoverUsd ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {profit?.sbp ? (
          <>
            <h2 className="vs-ops-subtitle">СБП — прибыль по магазинам</h2>
            <p className="vs-ops-table-scroll-hint">Прокрутите таблицу вправо →</p>
            <div className="vs-ops-table-wrap">
              <table className="vs-ops-table vs-ops-table--compact">
                <thead>
                  <tr>
                    <th>Магазин</th>
                    <th>Заказов</th>
                    <th>Оборот</th>
                    <th>Себестоимость</th>
                    <th>Прибыль</th>
                  </tr>
                </thead>
                <tbody>
                  {profit.sbp.byStore.map((row) => (
                    <tr key={row.storeId}>
                      <td>{row.storeId}</td>
                      <td>{row.ordersCount}</td>
                      <td>{formatRubPrice(row.revenueRub)}</td>
                      <td>{formatRubPrice(row.costRub)}</td>
                      <td>{formatRubPrice(row.profitRub)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {profit?.crypto ? (
          <>
            <h2 className="vs-ops-subtitle">Крипта — прибыль по магазинам</h2>
            <p className="vs-ops-table-scroll-hint">Прокрутите таблицу вправо →</p>
            <div className="vs-ops-table-wrap">
              <table className="vs-ops-table vs-ops-table--compact">
                <thead>
                  <tr>
                    <th>Магазин</th>
                    <th>Заказов</th>
                    <th>Оборот</th>
                    <th>Себестоимость</th>
                    <th>Прибыль</th>
                  </tr>
                </thead>
                <tbody>
                  {profit.crypto.byStore.map((row) => (
                    <tr key={row.storeId}>
                      <td>{row.storeId}</td>
                      <td>{row.ordersCount}</td>
                      <td>{formatUsdtPrice(row.revenueUsdt)}</td>
                      <td>{formatUsdtPrice(row.costUsdt)}</td>
                      <td>{formatUsdtPrice(row.profitUsdt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </VsOpsLayout>
    </VsOpsRequireAuth>
  );
}

export default VsOpsStatsPage;
